/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import React from "react";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { Box, Divider, Grid, Icon, IconButton, Typography } from "@mui/material";
import * as y from "yup";
import { HistoryBackButton } from "components/buttons";
import { SubmitButton } from "components/haven-design/form";
import { CheckboxWithErrorLabel } from "components/haven-design/form/checkbox";
import { MaterialFormikSelectAutocomplete } from "components/haven-design/form/select";
import { DatePickerField, InputWithErrorLabel } from "components/haven-design/form/inputs";
import FileUploadButton from "components/haven-design/file-upload-button";
import AddressFields from "components/common/form/address-fields";
import { AddressParts } from "utils/address";

export type ManualContractFormValues = AddressParts & {
  propertyId: string;
  organizationId: string;
  planId: string;
  signerUserId: string;
  unitCount: number | "";
  signedAt: Date | null;
  billingFrequency: string;
  contractStartDate: Date | null;
  initialTermEndDate: Date | null;
  propertyLegalName: string;
  status: string;
  propertyPhone: string;
  propertyEmail: string;
  // S14: post-sign toggle + signed PDF + optional subscription link.
  runPostSignSetup: boolean;
  signedPdfBase64: string;
  signedPdfFilename: string;
  propertyName: string;
  subscriptionId: string;
}

export type Option = {
  value: string;
  label: string;
}

export type PropertyOption = Option & AddressParts & {
  units: number | null;
  phone: string;
  email: string;
  organizationId: string;
}

type Props = {
  initialData: ManualContractFormValues;
  sending: boolean;
  propertyOptions: PropertyOption[];
  planOptions: Option[];
  signerOptions: Option[];
  organizationOptions: Option[];
  subscriptionOptions: Option[];
  handleSubmit: (
    values: ManualContractFormValues,
    helpers: Pick<FormikHelpers<ManualContractFormValues>, "setErrors" | "setTouched">,
  ) => void | Promise<void>;
  onPropertyChange: (organizationId: string) => void;
  onPropertyIdChange: (propertyId: string) => void;
  onOrganizationChange: (organizationId: string) => void;
}

export const BILLING_FREQUENCY_OPTIONS: Option[] = [
  { value: "annually", label: "Annually" },
  { value: "quarterly", label: "Quarterly" },
];

export const STATUS_OPTIONS: Option[] = [
  { value: "signed", label: "Signed" },
  { value: "draft", label: "Draft" },
];

const isSignedStatus = (status: unknown) => status === "signed";

// Callback form (not the `{ then }` object) so eslint's unicorn/no-thenable stays quiet.
const requiredWhenPostSign = (message: string) =>
  y.string().when("runPostSignSetup", ([on], schema) =>
    (on ? schema.required(message) : schema));

export const manualContractSchema = y.object().shape({
  runPostSignSetup: y.boolean(),
  // Toggle ON creates a draft property, so no existing property is selected.
  propertyId: y.string().when("runPostSignSetup", ([on], schema) =>
    (on ? schema : schema.required("Property is required"))),
  organizationId: requiredWhenPostSign("Organization is required"),
  propertyName: requiredWhenPostSign("Property name is required"),
  signerUserId: requiredWhenPostSign("Signer is required"),
  planId: y
    .string()
    .when("status", ([status], schema) =>
      (isSignedStatus(status) ? schema.required("Plan is required") : schema)),
  unitCount: y
    .number()
    // eslint-disable-next-line no-undefined -- yup needs undefined so empty input hits required()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("Units must be a number")
    .min(0, "Units must be 0 or more")
    .nullable()
    .when("status", ([status], schema) =>
      (isSignedStatus(status) ? schema.required("Units are required") : schema)),
  signedAt: y
    .date()
    .nullable()
    .when("status", ([status], schema) =>
      (isSignedStatus(status) ? schema.required("Sign date is required") : schema)),
  billingFrequency: y
    .string()
    .when("status", ([status], schema) =>
      (isSignedStatus(status) ? schema.required("Billing frequency is required") : schema)),
  contractStartDate: y
    .date()
    .nullable()
    .when("status", ([status], schema) =>
      (isSignedStatus(status) ? schema.required("Contract start date is required") : schema)),
  initialTermEndDate: y
    .date()
    .nullable()
    .when("status", ([status], schema) =>
      (isSignedStatus(status) ? schema.required("Contract end date is required") : schema)),
  propertyLegalName: y
    .string()
    .when("status", ([status], schema) =>
      (isSignedStatus(status) ? schema.required("Property legal name is required") : schema)),
  status: y.string().required("Status is required"),
  propertyPhone: requiredWhenPostSign("Property phone is required"),
  propertyEmail: y.string().email("Must be a valid email").when("runPostSignSetup", ([on], schema) =>
    (on ? schema.required("Property email is required") : schema)),
  address1: y.string(),
  address2: y.string(),
  city: y.string(),
  state: y.string(),
  zip: y.string(),
});

const PropertyAutoFill: React.FC<{
  propertyOptions: PropertyOption[];
  onPropertyChange: (organizationId: string) => void;
  onPropertyIdChange: (propertyId: string) => void;
}> = ({ propertyOptions, onPropertyChange, onPropertyIdChange }) => {
  const { values, setFieldValue } = useFormikContext<ManualContractFormValues>();
  const prevPropertyId = React.useRef(values.propertyId);

  React.useEffect(() => {
    if (values.propertyId === prevPropertyId.current) return;
    prevPropertyId.current = values.propertyId;
    onPropertyIdChange(values.propertyId);

    const selected = propertyOptions.find((p) => p.value === values.propertyId);
    if (!selected) return;

    setFieldValue("propertyLegalName", selected.label);
    setFieldValue("propertyName", selected.label);
    setFieldValue("unitCount", selected.units ?? "");
    setFieldValue("propertyPhone", selected.phone);
    setFieldValue("propertyEmail", selected.email);
    setFieldValue("address1", selected.address1);
    setFieldValue("address2", selected.address2);
    setFieldValue("city", selected.city);
    setFieldValue("state", selected.state);
    setFieldValue("zip", selected.zip);
    setFieldValue("organizationId", selected.organizationId);
    setFieldValue("signerUserId", "");
    setFieldValue("subscriptionId", "");
    onPropertyChange(selected.organizationId);
  }, [values.propertyId, propertyOptions, setFieldValue, onPropertyChange, onPropertyIdChange]);

  return null;
};

const dateLabel = (base: string, status: string) =>
  (isSignedStatus(status) ? `${base} *` : base);

const ContractDateFields: React.FC = () => {
  const { values } = useFormikContext<ManualContractFormValues>();
  const { status } = values;

  return (
    <>
      <Grid size={{ xs: 12, md: 6 }}>
        <DatePickerField
          margin="none"
          id="signedAt"
          name="signedAt"
          label={dateLabel("Sign date", status)}
          fullWidth
          small
          withError
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <DatePickerField
          margin="none"
          id="contractStartDate"
          name="contractStartDate"
          label={dateLabel("Contract start date", status)}
          fullWidth
          small
          withError
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <DatePickerField
          margin="none"
          id="initialTermEndDate"
          name="initialTermEndDate"
          label={dateLabel("Contract end date", status)}
          fullWidth
          small
          withError
        />
      </Grid>
    </>
  );
};

const OrganizationWatch: React.FC<{ onOrganizationChange: (organizationId: string) => void }> = ({
  onOrganizationChange,
}) => {
  const { values } = useFormikContext<ManualContractFormValues>();
  const prev = React.useRef(values.organizationId);

  React.useEffect(() => {
    if (!values.runPostSignSetup) return;
    if (values.organizationId === prev.current) return;
    prev.current = values.organizationId;
    onOrganizationChange(values.organizationId);
  }, [values.organizationId, values.runPostSignSetup, onOrganizationChange]);

  return null;
};

// The subscription selector is hidden under toggle-ON but toMutationInput still sends
// subscriptionId; clear a stale OFF selection when the toggle flips ON so it can't be submitted.
const PostSignSubscriptionReset: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<ManualContractFormValues>();

  React.useEffect(() => {
    if (values.runPostSignSetup && values.subscriptionId) {
      setFieldValue("subscriptionId", "");
    }
  }, [values.runPostSignSetup, values.subscriptionId, setFieldValue]);

  return null;
};

// haven-design/file-upload-button has no drag/drop support, so this is click-to-browse only.
const SignedPdfUpload: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<ManualContractFormValues>();
  // Bumped to remount the hidden input after a clear, so re-selecting the same
  // file still fires onChange (browsers skip onChange when value is unchanged).
  const [inputKey, setInputKey] = React.useState(0);
  // Remembers the status held before the first PDF upload (persisted status when
  // editing, form default when creating) so clear can restore it instead of
  // leaving the forced "signed" behind. null once there's no upload to undo.
  const preUploadStatus = React.useRef<string | null>(null);

  const readFile = (file?: File) => {
    if (!file) return;
    // Capture only on the first upload so replacing a file doesn't record "signed".
    if (preUploadStatus.current === null) preUploadStatus.current = values.status;
    const reader = new FileReader();
    reader.onload = () => {
      setFieldValue("signedPdfBase64", String(reader.result ?? ""));
      setFieldValue("signedPdfFilename", file.name);
      // A signed PDF means the contract is signed.
      setFieldValue("status", "signed");
    };
    reader.readAsDataURL(file);
  };

  const clearFile = () => {
    setFieldValue("signedPdfBase64", "");
    setFieldValue("signedPdfFilename", "");
    // Revert the status forced by upload, exactly once, back to its pre-upload value.
    if (preUploadStatus.current !== null) {
      setFieldValue("status", preUploadStatus.current);
      preUploadStatus.current = null;
    }
    setInputKey((key) => key + 1);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <FileUploadButton
        key={inputKey}
        id="signed-pdf-input"
        name="signedPdf"
        accept="application/pdf"
        onChange={(e) => readFile(e.target.files?.[0])}
      />
      <Typography variant="body2">
        {values.signedPdfFilename ? `Selected: ${values.signedPdfFilename}` : "No file selected"}
      </Typography>
      {values.signedPdfFilename && (
        <IconButton aria-label="clear signed pdf" size="small" onClick={clearFile}>
          <Icon>close</Icon>
        </IconButton>
      )}
    </Box>
  );
};

const ManualContractForm: React.FC<Props> = ({
  initialData,
  sending,
  propertyOptions,
  planOptions,
  signerOptions,
  organizationOptions,
  subscriptionOptions,
  handleSubmit,
  onPropertyChange,
  onPropertyIdChange,
  onOrganizationChange,
}) => (
  <Formik
    initialValues={initialData}
    validationSchema={manualContractSchema}
    validateOnBlur
    onSubmit={(values, helpers) =>
      handleSubmit(values, { setErrors: helpers.setErrors, setTouched: helpers.setTouched })
    }
  >
    {({ isSubmitting, values }) => (
      <Form>
        <PropertyAutoFill
          propertyOptions={propertyOptions}
          onPropertyChange={onPropertyChange}
          onPropertyIdChange={onPropertyIdChange}
        />
        <OrganizationWatch onOrganizationChange={onOrganizationChange} />
        <PostSignSubscriptionReset />
        <Grid container className="card" size={{ xs: 12 }}>
          <Grid container spacing={2} size={{ xs: 12, lg: 8, xl: 6 }}>
            <Grid size={{ xs: 12 }}>
              <CheckboxWithErrorLabel
                name="runPostSignSetup"
                label="Run post-sign setup steps (creates a draft property, subscription, access grants & notifications)"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">Property</Typography>
            </Grid>
            {values.runPostSignSetup ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <MaterialFormikSelectAutocomplete
                  id="organizationId"
                  margin="none"
                  name="organizationId"
                  placeholder="Organization"
                  title="Organization"
                  options={organizationOptions}
                />
              </Grid>
            ) : (
              <Grid size={{ xs: 12, md: 6 }}>
                <MaterialFormikSelectAutocomplete
                  id="propertyId"
                  margin="none"
                  name="propertyId"
                  placeholder="Property"
                  title="Property"
                  options={propertyOptions}
                />
              </Grid>
            )}
            {values.runPostSignSetup && (
              <Grid size={{ xs: 12, md: 6 }}>
                <InputWithErrorLabel name="propertyName" label="Property name" margin="none" size="small" />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <InputWithErrorLabel name="propertyLegalName" label="Property legal name" margin="none" size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <InputWithErrorLabel name="unitCount" label="Units" margin="none" size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <InputWithErrorLabel
                name="propertyPhone"
                label={values.runPostSignSetup ? "Property phone" : "Property phone (optional)"}
                margin="none"
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <InputWithErrorLabel
                name="propertyEmail"
                label={values.runPostSignSetup ? "Property email" : "Property email (optional)"}
                margin="none"
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">Property Address</Typography>
            </Grid>
            <AddressFields required={false} />
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">Signed contract PDF</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SignedPdfUpload />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">Details</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MaterialFormikSelectAutocomplete
                id="planId"
                margin="none"
                name="planId"
                placeholder="Plan"
                title="Plan"
                options={planOptions}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MaterialFormikSelectAutocomplete
                id="billingFrequency"
                margin="none"
                name="billingFrequency"
                placeholder="Billing frequency"
                title="Billing frequency"
                options={BILLING_FREQUENCY_OPTIONS}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MaterialFormikSelectAutocomplete
                id="signerUserId"
                margin="none"
                name="signerUserId"
                placeholder={values.runPostSignSetup ? "Signer" : "Signer (optional)"}
                title={values.runPostSignSetup ? "Signer" : "Signer (optional)"}
                options={signerOptions}
              />
            </Grid>
            {!values.runPostSignSetup && (
              <Grid size={{ xs: 12, md: 6 }}>
                <MaterialFormikSelectAutocomplete
                  id="subscriptionId"
                  margin="none"
                  name="subscriptionId"
                  placeholder="Link subscription (optional)"
                  title="Link subscription (optional)"
                  options={subscriptionOptions}
                />
              </Grid>
            )}
            <ContractDateFields />
            <Grid size={{ xs: 12, md: 6 }}>
              <MaterialFormikSelectAutocomplete
                id="status"
                margin="none"
                name="status"
                placeholder="Status"
                title="Status"
                options={STATUS_OPTIONS}
              />
            </Grid>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <HistoryBackButton marginRight size="L" />
          <SubmitButton
            isWide
            text="Save"
            loadingText="Saving"
            isLoading={isSubmitting || sending}
          />
        </Box>
      </Form>
    )}
  </Formik>
);

export default ManualContractForm;
